import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { enableMapSet } from "immer";
import { getUseCaseForTrigger, getTriggerForUseCase } from "src/util/commonUtil";
import * as solutionConfig from "src/config/solutionConfigs.json";
import {APICallStatus} from "src/enum/enums";

enableMapSet();

export interface SolutionState {
  type: string;
  solutionDetailObject: Record<any, any>;
}

export interface EventModelState {
  trigger: string;
  clientUseCase: string;
  bindleId: string;
  eventName: string;
  eventRule: string;
  validFrom: string;
  validTill: string;
  eventSolution: SolutionState;
  promoteToProdStatus: string;
  promoteToProdError: string;
  contentId?: string;
  contentPath?: string;
}

const initialState: EventModelState = {
  trigger: "",
  clientUseCase: "",
  eventName: "",
  bindleId: "",
  eventRule: "",
  validFrom: "",
  validTill: "",
  eventSolution: {
    type: "",
    solutionDetailObject: {},
  },
  promoteToProdStatus: APICallStatus.NOT_STARTED.toString(),
  promoteToProdError: "",
  contentId: "",
  contentPath: "",
}

/**
 * Slice for event model redux state.
 */
export const eventModelSlice = createSlice({
  name: "eventModel",
  initialState,
  reducers: {
    updateTrigger: (state, action: PayloadAction<string>) => {
      state.trigger = action.payload;
      state.clientUseCase = getUseCaseForTrigger(action.payload);
    },

    updateClientUseCase: (state, action: PayloadAction<string>) => {
      state.clientUseCase = action.payload;
      state.trigger = getTriggerForUseCase(action.payload);
    },

    updateEventName: (state, action: PayloadAction<string>) => {
      state.eventName = action.payload;
    },

    updateBindleId: (state, action: PayloadAction<string>) => {
      state.bindleId = action.payload;
    },

    updateEventRule: (state, action: PayloadAction<string>) => {
      state.eventRule = action.payload;
    },

    updateValidFrom: (state, action: PayloadAction<string>) => {
      state.validFrom = action.payload;
    },

    updateValidTill: (state, action: PayloadAction<string>) => {
      state.validTill = action.payload;
    },

    updateEventSolutionType: (state, action: PayloadAction<string>) => {
      state.eventSolution.type = action.payload;
      // update solution detail object according to the solution type
      state.eventSolution.solutionDetailObject = solutionConfig[action.payload]?.solutionDetailsTemplate;
    },

    updateEventSolutionDetailsObject: (state, action: PayloadAction<Record<any, any>>) => {
      state.eventSolution.solutionDetailObject = {
        ...state.eventSolution.solutionDetailObject,
        ...action.payload,
      }
    },

    updateEventModel: (state, action: PayloadAction<EventModelState>) => {
      const newEventModelState = action.payload;

      if (newEventModelState?.clientUseCase) {
        newEventModelState.trigger = getTriggerForUseCase(newEventModelState.clientUseCase);
      } else if (newEventModelState?.trigger) {
        newEventModelState.clientUseCase = getUseCaseForTrigger(newEventModelState.trigger);
      }

      return newEventModelState;
    },

    resetEventModelState: (state, action: PayloadAction<void>) => {
      return initialState;
    },
    updatePromoteToProdStatus: (state, action: PayloadAction<string>) =>{
      state.promoteToProdStatus = action.payload;
    },
    updatePromoteToProdError: (state, action: PayloadAction<string>) =>{
      state.promoteToProdError = action.payload;
    },
    updateContentId: (state, action: PayloadAction<string>) => {
      state.contentId = action.payload;
    },

    updateContentPath: (state, action: PayloadAction<string>) => {
      state.contentPath = action.payload;
    },
  }
});

export const {
  updateTrigger,
  updateEventName,
  updateBindleId,
  updateEventRule,
  updateValidFrom,
  updateValidTill,
  updateEventSolutionType,
  updateEventSolutionDetailsObject,
  resetEventModelState,
  updateEventModel,
  updatePromoteToProdStatus,
  updatePromoteToProdError,
  updateContentId,
  updateContentPath,
} = eventModelSlice.actions;
